import * as v0 from "@aidkitorg/types/lib/survey";
import { classNames } from "../Util";
import { BeakerIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useState } from "react";
import { usePost, useToken } from "../API";
import { useToast } from "@aidkitorg/component-library";
import { InfoDict } from "../Questions/Props";
import { AuthContext } from "../Context";
import { AllDoneComponent, Sections } from "../Apply";
import { ModularQuestionPage } from "../ModularQuestionPage";
import { AirtableSurveyToSurveyDefinition, expandTemplates, v0ToLegacy } from "@aidkitorg/types/lib/translation/v0_to_legacy";
import { PersonaModal } from "../PersonaModal";
import { State } from "@aidkitorg/typesheets/lib/collab";
import { ApplicantProvider } from "../Contexts/ApplicantContext";
import { Dropdown } from "../Components/Dropdown";
import { AICoachProvider } from "../AICoach/AICoachContext";
import { ViewportProvider } from "../Contexts/ViewportContext";

const PersonaCreator = ({ personas, setActivePersonaName }: {
  personas: v0.Persona[],
  setActivePersonaName: (activePersonaName: string | null) => void
}) => {

  const personaApi = usePost('/bots/persona', { handleErrors: () => { } });
  const [generating, setGenerating] = useState(false);
  const { toast } = useToast();

  async function createPersona(retries: number = 0, background?: string): Promise<v0.Persona> {
    let result: v0.Persona | { error: string } = null as any;
    try {
      result = await personaApi({ background });
      if ('error' in result) {
        throw new Error((result as { error: string }).error);
      } else {
        return result;
      }
    } catch (e) {
      if (!retries) {
        toast({
          description: "Failed to create persona, please try again later",
          variant: 'error'
        })
        throw e;
      }
      return await createPersona(Math.max(0, retries - 1));
    }
  }

  async function generateTempPersona() {
    setGenerating(true);
    const yn = prompt('how many should I create?');
    const persons = yn ? parseInt(yn) : 0;
    if (persons) {
      const personasToMake = Array(persons).fill(null).map(() => createPersona(5));
      const successful = (await Promise.allSettled(personasToMake)).filter(p => p.status === 'fulfilled');
      personas.push(...successful.map((p: any) => p.value));
      setActivePersonaName((successful[0] as any)?.value?.name);
    }
    setGenerating(false);
  }

  return <button
    onClick={generateTempPersona}
    className={classNames(
      "h-9 rounded-l px-2.5 py-1.5 cursor-pointer bg-gray-100 hover:bg-gray-200 transition-all font-medium border border-gray-500",
      generating ? "from-green-300 to-green-800 bg-gradient-to-b animate-pulse" : null,
    )}
  >
    <BeakerIcon className={classNames(
      "w-5 h-5 text-indigo-800"
    )} />
  </button>
}

export default function SurveyPreview({ survey, mockInfo, setMockInfo,
  activeSurvey, setActiveSurvey, subsurveys, personas, state }: {
  survey: v0.ExpandedSurvey,
  mockInfo: InfoDict,
  setMockInfo: React.Dispatch<React.SetStateAction<InfoDict>>,
  /** Path of the survey to preview. Null means Global View, undefined means default to first subsurvey. */
  activeSurvey: string | null | undefined,
  setActiveSurvey: (path: string | null) => void,
  // this will eventually move into this component, for now
  // it's passed in from the parent to maintain equivalent functionality as we refactor
  subsurveys: v0.Subsurvey[] | null,
  personas: v0.Persona[] | null,
  state: State
}) {

  const [, token] = useToken();
  const [activePersonaName, setActivePersonaName] = useState<string | null>(null);
  const [showPersonaModal, setShowPersonaModal] = useState(false);
  const [mockAllDone, setMockAllDone] = useState(false);
  const [mockViewer, setMockViewer] = useState<'applicant' | 'admin'>('applicant');
  const [recompute, setRecompute] = useState(0);
  const [translated, setTranslated] = useState<Sections | null>(null);
  const [customSubmittedModal, setCustomSubmittedModal] = useState<any>(null);


  const MockSubmit = async (submit_key?: string, options?: { info: InfoDict }) => {
    const submitInfo = options?.info ? options.info : mockInfo;
    console.log("Submitting", submitInfo);
    setMockInfo((prevInfo) => ({
      ...prevInfo,
      [submit_key!]: new Date().toISOString()
    }));
    setMockAllDone(true);
  }

  useEffect(() => {
    const matchedPersona: v0.Persona = personas?.find((p) => p.name === activePersonaName) as v0.Persona || {};
    // Clear the active persona if its name changes
    // TODO: Use a stable reference for the persona so we don't need to do this
    if (!matchedPersona.attrs) {
      setActivePersonaName('');
    } else {
      const activePersonaMockInfo: Record<string, string> = {};
      for (let { field, value } of (matchedPersona.attrs || [])) {
        activePersonaMockInfo[field] = value;
      }
      setMockInfo(activePersonaMockInfo);
    }

    setRecompute((prevState) => prevState + 1);
  }, [activePersonaName, personas]);

  useEffect(() => {
    if (!survey) return;

    let s: v0.Survey;
    
    if (!activeSurvey) {
      if (activeSurvey === undefined && subsurveys?.length) {
        s = subsurveys[0].sections;
        setCustomSubmittedModal(subsurveys[0].customSubmittedModal);
      } else {
        s = Array.isArray(survey) ? survey : survey.survey;
      }
    } else {
      const targetSubsurvey = subsurveys?.find((s) => s.path === activeSurvey);
      s = targetSubsurvey?.sections || [];
      setCustomSubmittedModal(targetSubsurvey?.customSubmittedModal);
    }
    
    // Construct a Root object with the survey sections we want to translate
    // but include config from the parent survey so enableLastModifiedInJS is available
    const rootForTranslation: v0.Root = Array.isArray(survey) 
      ? s  // If survey is an array, just use s (no config available)
      : { survey: s, config: survey.config, personas: survey.personas, definitions: survey.definitions }; // Include config, personas, and definitions from parent ExpandedSurvey
    
    let airtableSurvey = v0ToLegacy(rootForTranslation, { includeConfig: false });
    setTranslated(AirtableSurveyToSurveyDefinition(airtableSurvey) as unknown as Sections);
  }, [subsurveys, survey, activeSurvey]);

  return <>
    <Dropdown
      className="h-9 w-full text-gray-600 rounded border-2 px-2.5 py-1.5 cursor-pointer bg-gray-100 
          hover:bg-gray-200 transition-all font-medium border border-gray-500 ml-1 mt-1" 
      label={(activeSurvey === undefined ? subsurveys?.[0]?.path : activeSurvey ) || 'Global View'} options={[
        { label: 'Global View', callback: () => setActiveSurvey(null) },
        ...((subsurveys || []).map((s) => ({
          label: s.path,
          callback: () => setActiveSurvey(s.path)
        })))
      ]}
      description="Subsurvey to Preview"
    />
    <div className="flex justify-between items-center text-gray-600 mt-2 mb-1 mr-3 ml-1">
      <button
        className="h-9 rounded border-2 px-2.5 py-1.5 cursor-pointer bg-gray-100 
          hover:bg-gray-200 transition-all font-medium border border-gray-500
          overflow-clip"
        onClick={() => setShowPersonaModal(true)}
      >
        {!activePersonaName ? "Save as New Persona" : "Update Persona Or Save as New"}
      </button>
      <div className="flex" style={{ display: !!personas?.length ? 'flex' : 'none' }}>
        <PersonaCreator personas={personas || []} setActivePersonaName={setActivePersonaName} />
        <select
          id="persona-selector"
          value={activePersonaName || ""}
          disabled={!personas?.length}
          onChange={(e) => {
            setActivePersonaName(e.target.value)
            if (!e.target.value) setMockInfo({});
          }}
          className={
            "h-9 rounded-r border-2 px-2.5 py-1.5 transition-all " +
            (!activePersonaName
              ? "bg-gray-100 hover:bg-gray-100 border-gray-500 border font-medium "
              : "bg-emerald-500 hover:bg-emerald-500 border-emerald-600 text-white font-semibold shadow-sm ") +
            (personas?.length ? "cursor-pointer " : "")
          }
        >
          {!activePersonaName ? (
            <option value="" disabled>View as Persona</option>
          ) : (
            <option value="">Clear Persona</option>
          )}
          {personas?.map((p, i) => (
            <option key={i} value={p.name}>{p.name}</option>
          ))}
        </select>
      </div>
    </div>
    {showPersonaModal && (
      <PersonaModal
        state={state}
        mockInfo={mockInfo}
        showModal={showPersonaModal}
        setShowModal={setShowPersonaModal}
        activePersonaName={activePersonaName}
        setActivePersonaName={setActivePersonaName}
      />
    )}
    <AuthContext.Provider value={{
      localId: () => {
        return 'foo';
      },
      token: () => {
        return token.replace('auth=', '');
      },
      setToken: (token) => { },
      setLocalId: (id) => { }
    }}>
      <ApplicantProvider context={{ info: mockInfo, update: setMockInfo }}>
        {mockAllDone &&
          <AllDoneComponent 
            content={<></>} 
            info={mockInfo}
            customSubmittedModal={customSubmittedModal}
          />
        }
        {translated &&
            <ViewportProvider>
              {(mockViewer === 'applicant' ?
                <ModularQuestionPage
                  sections={translated as any}
                  submit={MockSubmit}
                  noHistory={true}
                  sequential={true}
                  recompute={recompute}
                />
                :
                <ModularQuestionPage
                  sections={translated as any}
                  noHistory={true}
                  sequential={true} />
              )}
            </ViewportProvider>
        }
      </ApplicantProvider>
    </AuthContext.Provider>
  </>
}
